import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { useLang, tField } from "../lib/LanguageContext";
import { api, imageUrl } from "../lib/api";
import SEO from "../components/SEO";

export default function Facilities() {
    const { t, lang } = useLang();
    const { site } = useOutletContext() || {};
    const [items, setItems] = useState([]);

    useEffect(() => {
        api.get("/facilities").then((r) => setItems(r.data)).catch(() => {});
    }, []);

    return (
        <div data-testid="facilities-page">
            <SEO
                title={lang === "al" ? "Lehtësirat" : "Facilities"}
                description={lang === "al" ? "Restorant me pamje deti, bar elegant dhe market në Kompleksin Dea Radhimë." : "Seaview restaurant, elegant bar and on-site market at Kompleksi Dea Radhimë."}
                path="/facilities"
            />
            {/* Page Header */}
            <section className="relative h-[55vh] min-h-[400px] flex items-end overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url(${imageUrl(site?.facilities_hero_image, imageUrl("seed:facility_restaurant"))})`,
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/40" />
                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-16 pt-32 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                    >
                        <span className="text-xs tracking-[0.3em] uppercase text-white/80">
                            {t("nav_facilities")}
                        </span>
                        <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3">
                            {t("explore_facilities")}
                        </h1>
                        <p className="text-white/85 font-light text-lg mt-4 max-w-2xl">
                            {t("facilities_intro")}
                        </p>
                    </motion.div>
                </div>
            </section>

            <section className="py-20 md:py-28 px-6 md:px-12 max-w-7xl mx-auto">
                <div className="flex flex-col gap-20 md:gap-32">
                    {items.map((f, i) => {
                        const Icon = Icons[f.icon] || Icons.Star;
                        const reverse = i % 2 === 1;
                        return (
                            <motion.div
                                key={f.id}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7 }}
                                data-testid={`facility-${f.slug}`}
                                className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}
                            >
                                <div className="aspect-[5/4] rounded-2xl overflow-hidden shadow-[0_20px_60px_rgb(0,0,0,0.08)]">
                                    <img decoding="async" loading="lazy"
                                        src={imageUrl(
                                            f.image,
                                            imageUrl("seed:facility_restaurant"),
                                        )}
                                        alt={tField(f, "title", lang)}
                                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                    />
                                </div>
                                <div>
                                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#F4EFE6] mb-6">
                                        <Icon
                                            size={26}
                                            strokeWidth={1.5}
                                            className="text-[#0F3A59]"
                                        />
                                    </div>
                                    <span className="text-xs tracking-[0.3em] uppercase text-[#0F3A59]/60">
                                        0{i + 1}
                                    </span>
                                    <h2 className="font-serif text-4xl md:text-5xl text-[#0F3A59] tracking-tight mt-2">
                                        {tField(f, "title", lang)}
                                    </h2>
                                    <p className="text-[#1C1C1C]/75 font-light mt-6 leading-relaxed text-lg">
                                        {tField(f, "description", lang)}
                                    </p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
